/**
 * Surveys List component for managing user's surveys
 * Access: admins and internal BluePill team members (@blue-pill.ai)
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Plus, FileText, Calendar, Eye, ExternalLink, Search, X, Copy } from 'lucide-react';
import Button from '../../components/Button';
import Layout from '../../components/Layout';
import {
  getUserSurveys,
  Survey,
  surveyIdToShareId,
  getSurveyGroups,
  SurveyGroup,
  duplicateSurvey,
} from '../../utils/surveyApi';
import { SURVEY_STATES } from '../../constants/surveyConstants';
import { surveyStateBadgeClass } from './surveyStateBadge';
import { UI_TEXT } from '../../config/ui-text';
import { getGroupColor } from '../../components/survey/GroupMediaUpload';
import { useAuth } from '../../context/AuthContext';

// State filter pills, in display order: 'all' (which carries no SURVEY_STATES entry of its
// own) plus one pill per state the product defines. Derived rather than re-listed so a state
// added to SURVEY_STATES gets a pill, instead of counting toward 'All' and toward no pill —
// which silently breaks the invariant that the pill counts sum to the 'All' count.
const STATE_FILTER_OPTIONS = ['all', ...Object.values(SURVEY_STATES)] as const;
type StateFilter = (typeof STATE_FILTER_OPTIONS)[number];

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Wraps the first case-insensitive match of `query` in `name` with a <mark>. */
const highlightMatch = (name: string, query: string): React.ReactNode => {
  const q = query.trim();
  if (!q) return name;
  const i = name.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return name;
  return (
    <>
      {name.slice(0, i)}
      <mark className="bg-primary/30 rounded px-0.5">{name.slice(i, i + q.length)}</mark>
      {name.slice(i + q.length)}
    </>
  );
};

const SurveysList: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin, isInternalUser } = useAuth();
  // Declared ahead of the auth-gate early return below (pre-existing pattern in this
  // component) so adding them doesn't add to its conditional-hook-call surface.
  const [searchQuery, setSearchQuery] = useState('');
  const [stateFilter, setStateFilter] = useState<StateFilter>('all');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Redirect users without survey access (admins + internal team) to home
  if (!isAdmin && !isInternalUser) {
    return <Navigate to="/" replace />;
  }
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [surveyGroups, setSurveyGroups] = useState<SurveyGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The id being copied, not a boolean, so the busy label lands on the card that is actually
  // copying. One at a time is deliberate: a single id cannot track two in-flight copies, and the
  // second would clear the first's marker and un-disable its button mid-request.
  const [duplicatingId, setDuplicatingId] = useState<number | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const [surveysResult, groupsResult] = await Promise.all([
          getUserSurveys(),
          getSurveyGroups().catch(() => ({ groups: [] })),
        ]);
        const groupSurveyIds = new Set(groupsResult.groups.flatMap((g) => g.survey_ids));
        setSurveys(surveysResult.surveys.filter((s) => !groupSurveyIds.has(s.id)));
        setSurveyGroups(groupsResult.groups);
      } catch (error) {
        console.error('Failed to load surveys:', error);
        setError(UI_TEXT.HUMAN_SURVEY.LIST.ERRORS.FAILED_TO_LOAD);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Combined and sorted list of surveys and groups
  const sortedItems = useMemo(() => {
    const surveyItems = surveys.map((s) => ({
      type: 'survey' as const,
      data: s,
      created_at: s.created_at,
    }));
    const groupItems = surveyGroups.map((g) => ({
      type: 'group' as const,
      data: g,
      created_at: g.created_at,
    }));
    return [...surveyItems, ...groupItems].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
  }, [surveys, surveyGroups]);

  /**
   * Copy a survey and show the result immediately.
   *
   * The reply is prepended rather than refetched: the copy's `created_at` is now, so it sorts to
   * the front of the same list the loader builds, and a refetch would cost a round trip to learn
   * something the reply already said.
   *
   * Disabling the controls while one is in flight is what stops a second click landing a second
   * copy. The request is not idempotent and never can be — duplicating twice on purpose is a
   * legitimate act — so nothing downstream would catch it.
   */
  const handleDuplicate = async (surveyId: number) => {
    setDuplicatingId(surveyId);
    try {
      const { survey } = await duplicateSurvey(surveyId);
      setSurveys((prev) => [survey, ...prev]);
      setError(null);
    } catch (e) {
      console.error('Failed to duplicate survey:', e);
      setError(UI_TEXT.HUMAN_SURVEY.LIST.ERRORS.FAILED_TO_DUPLICATE);
    } finally {
      setDuplicatingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getSurveyDisplayName = (survey: Survey) => {
    if (survey.name && survey.name.trim()) {
      return survey.name;
    }
    // Generate a name using the survey ID
    return `Survey #${survey.id}`;
  };

  const itemName = (item: (typeof sortedItems)[number]) =>
    item.type === 'group' ? item.data.name : getSurveyDisplayName(item.data);

  // A group's card always renders a hardcoded "active" badge (see the group branch below,
  // unchanged) — so its effective state for filtering/counting purposes is ACTIVE. One
  // helper, used by both stateCounts and shownItems, so the badge and the filter can't drift.
  const effectiveState = (item: (typeof sortedItems)[number]): string =>
    item.type === 'group' ? SURVEY_STATES.ACTIVE : item.data.state;

  // Plain values, not memoized: at most 69 items, so filtering/counting on every render
  // is negligible next to the network round-trip that already gates this render.
  // Search is applied first; state-filter pill counts are computed from its result so the
  // two controls visibly compose (matches the approved mock).
  const searchQueryTrimmed = searchQuery.trim().toLowerCase();
  const searchFiltered = searchQueryTrimmed
    ? sortedItems.filter((item) => itemName(item).toLowerCase().includes(searchQueryTrimmed))
    : sortedItems;

  const stateCounts = { all: searchFiltered.length } as Record<StateFilter, number>;
  STATE_FILTER_OPTIONS.forEach((state) => {
    if (state === 'all') return;
    stateCounts[state] = searchFiltered.filter((item) => effectiveState(item) === state).length;
  });

  const shownItems =
    stateFilter === 'all'
      ? searchFiltered
      : searchFiltered.filter((item) => effectiveState(item) === stateFilter);

  const isFiltering = searchQuery.trim() !== '' || stateFilter !== 'all';
  // '' (not 'all') when no specific state is selected, so NO_MATCH's copy functions can
  // branch on "is a state filter actually narrowing this?" with a plain truthiness check.
  const stateLabel = stateFilter === 'all' ? '' : capitalize(stateFilter);

  const clearFilters = () => {
    setSearchQuery('');
    setStateFilter('all');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{UI_TEXT.HUMAN_SURVEY.LIST.LOADING.LOADING}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {UI_TEXT.HUMAN_SURVEY.LIST.TITLES.HUMAN_SURVEYS}
              </h1>
              <p className="text-gray-600">{UI_TEXT.HUMAN_SURVEY.LIST.DESCRIPTIONS.MAIN}</p>
            </div>
            <Button
              variant="primary"
              onClick={() => navigate('/surveys/new')}
              icon={<Plus className="w-4 h-4" />}
            >
              {UI_TEXT.HUMAN_SURVEY.LIST.BUTTONS.CREATE_SURVEY}
            </Button>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {/* Surveys and Groups Grid - Combined and Sorted by Creation Date */}
          {sortedItems.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {UI_TEXT.HUMAN_SURVEY.LIST.TITLES.NO_SURVEYS}
              </h3>
              <p className="text-gray-600 mb-6">
                {UI_TEXT.HUMAN_SURVEY.LIST.DESCRIPTIONS.NO_SURVEYS}
              </p>
              <Button
                variant="primary"
                onClick={() => navigate('/surveys/new')}
                icon={<Plus className="w-4 h-4" />}
              >
                {UI_TEXT.HUMAN_SURVEY.LIST.BUTTONS.CREATE_FIRST_SURVEY}
              </Button>
            </div>
          ) : (
            <>
              {/* Search + state-filter controls (spec 42, PR 1) */}
              <div className="bg-white border border-gray-200 rounded-lg p-3.5 mb-5 flex flex-wrap items-center gap-3 shadow-sm">
                <div className="relative flex-1 min-w-[14rem]">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="search"
                    autoComplete="off"
                    placeholder={UI_TEXT.HUMAN_SURVEY.LIST.SEARCH.PLACEHOLDER}
                    aria-label={UI_TEXT.HUMAN_SURVEY.LIST.SEARCH.ARIA_LABEL}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      aria-label={UI_TEXT.HUMAN_SURVEY.LIST.SEARCH.CLEAR_ARIA_LABEL}
                      onClick={() => {
                        setSearchQuery('');
                        // Keyboard/screen-reader users shouldn't lose their place after
                        // clearing — put focus back where they were, as the mock does.
                        searchInputRef.current?.focus();
                      }}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-50"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <div
                  role="group"
                  aria-label={UI_TEXT.HUMAN_SURVEY.LIST.FILTERS.ARIA_LABEL}
                  className="flex flex-wrap gap-1.5"
                >
                  {STATE_FILTER_OPTIONS.map((state) => (
                    <button
                      key={state}
                      type="button"
                      aria-pressed={stateFilter === state}
                      onClick={() => setStateFilter(state)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors ${
                        stateFilter === state
                          ? 'bg-primary border-primary text-gray-900 font-semibold'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {state === 'all' ? UI_TEXT.HUMAN_SURVEY.LIST.FILTERS.ALL : capitalize(state)}{' '}
                      <span className="tabular-nums opacity-70 text-xs">{stateCounts[state]}</span>
                    </button>
                  ))}
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                {isFiltering
                  ? UI_TEXT.HUMAN_SURVEY.LIST.RESULTS.SHOWING(shownItems.length, sortedItems.length)
                  : UI_TEXT.HUMAN_SURVEY.LIST.RESULTS.TOTAL(sortedItems.length)}
              </p>

              {shownItems.length === 0 ? (
                <div className="text-center py-12">
                  <Search className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {UI_TEXT.HUMAN_SURVEY.LIST.NO_MATCH.TITLE(searchQuery, stateLabel)}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {UI_TEXT.HUMAN_SURVEY.LIST.NO_MATCH.BODY(
                      searchQuery,
                      stateLabel,
                      sortedItems.length,
                    )}
                  </p>
                  <Button variant="outline" onClick={clearFilters}>
                    {UI_TEXT.HUMAN_SURVEY.LIST.NO_MATCH.CLEAR_FILTERS}
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {shownItems.map((item) => {
                    if (item.type === 'group') {
                      const group = item.data as SurveyGroup;
                      return (
                        <div
                          key={`group-${group.id}`}
                          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                {highlightMatch(group.name, searchQuery)}
                              </h3>
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                active
                              </span>
                            </div>
                          </div>

                          {/* Group tags with colors */}
                          <div className="flex flex-wrap gap-1.5 mb-3">
                            {group.group_labels.map((label: string, idx: number) => {
                              const colors = getGroupColor(idx);
                              return (
                                <span
                                  key={idx}
                                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors.bg} ${colors.text} ${colors.border} border`}
                                >
                                  {label}
                                </span>
                              );
                            })}
                          </div>

                          <div className="space-y-2 mb-4">
                            <div className="flex items-center text-sm text-gray-600">
                              <Calendar className="w-4 h-4 mr-2" />
                              {UI_TEXT.HUMAN_SURVEY.LIST.LABELS.CREATED}{' '}
                              {formatDate(group.created_at)}
                            </div>
                          </div>

                          <div className="flex space-x-2">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() =>
                                window.open(
                                  `${import.meta.env.VITE_API_URL || ''}/api/survey-groups/distribute/${group.id}`,
                                  '_blank',
                                  'noopener,noreferrer',
                                )
                              }
                              icon={<ExternalLink className="w-3.5 h-3.5" />}
                            >
                              Take Survey
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/survey-groups/${group.id}`)}
                            >
                              {UI_TEXT.HUMAN_SURVEY.LIST.BUTTONS.VIEW_DETAILS}
                            </Button>
                          </div>
                        </div>
                      );
                    } else {
                      const survey = item.data as Survey;
                      return (
                        <div
                          key={survey.id}
                          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                {highlightMatch(getSurveyDisplayName(survey), searchQuery)}
                              </h3>
                              <div className="flex items-center space-x-2 mb-2">
                                <span
                                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${surveyStateBadgeClass(survey.state)}`}
                                >
                                  {survey.state}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="space-y-2 mb-4">
                            <div className="flex items-center text-sm text-gray-600">
                              <Calendar className="w-4 h-4 mr-2" />
                              {UI_TEXT.HUMAN_SURVEY.LIST.LABELS.CREATED}{' '}
                              {formatDate(survey.created_at)}
                            </div>
                            {survey.published_at && (
                              <div className="flex items-center text-sm text-gray-600">
                                <Eye className="w-4 h-4 mr-2" />
                                {UI_TEXT.HUMAN_SURVEY.LIST.LABELS.PUBLISHED}{' '}
                                {formatDate(survey.published_at)}
                              </div>
                            )}
                          </div>

                          <div className="flex space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/surveys/${survey.id}`)}
                            >
                              {UI_TEXT.HUMAN_SURVEY.LIST.BUTTONS.VIEW_DETAILS}
                            </Button>
                            {survey.state === SURVEY_STATES.ACTIVE && (
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() =>
                                  window.open(
                                    `/surveys/${surveyIdToShareId(survey.id)}/take`,
                                    '_blank',
                                    'noopener,noreferrer',
                                  )
                                }
                              >
                                {UI_TEXT.HUMAN_SURVEY.LIST.BUTTONS.TAKE_SURVEY}
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                window.open(
                                  `/surveys/${surveyIdToShareId(survey.id)}/take?preview=true`,
                                  '_blank',
                                  'noopener,noreferrer',
                                )
                              }
                              icon={<Eye className="w-3.5 h-3.5" />}
                            >
                              Preview
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={duplicatingId !== null}
                              onClick={() => handleDuplicate(survey.id)}
                              icon={<Copy className="w-3.5 h-3.5" />}
                            >
                              {duplicatingId === survey.id
                                ? UI_TEXT.HUMAN_SURVEY.LIST.BUTTONS.DUPLICATING
                                : UI_TEXT.HUMAN_SURVEY.LIST.BUTTONS.DUPLICATE}
                            </Button>
                          </div>
                        </div>
                      );
                    }
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default SurveysList;
